'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";

import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button'

import {PersonFill, Google, Facebook} from "react-bootstrap-icons";
import {WashingtonW} from "./CustomIcons";

import appStore from './AppStore';
import UnknownState from './UnknownState';
import Broken from './Broken';
import {States, IdentityProviders} from './AppConstants';
import DocumentTitle from './DocumentTitle';
import PropTypes from "prop-types";

/**
 * Gathers login info and tries to log the user in.
 */
class Login extends Component {
    constructor(props) {
        super(props);
        this.store = appStore;
    }

    loginNetid = () => this.store.login(IdentityProviders.netId.id);
    loginGoogle = () => this.store.login(IdentityProviders.google.id);
    loginFacebook = () => this.store.login(IdentityProviders.facebook.id);

    render() {
        let component;
        switch (this.store.state) {
            case States.pending:
                 component =
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>;
            case States.error:
                component = <Broken name="Login"/>;
                break;
            case States.done:
                component = <div className="p-5 fs-4 text-center">You are logged in! But you shouldn't be seeing this.</div>;
                break;
            case States.unauthenticated:
                component =
                    <DocumentTitle title="Login">
                        <div className="app-wrapper">
                            <header role="banner">
                                <Navbar bg="dark" variant="dark" className="navbar-custom justify-content-between d-print-none">
                                    <Nav aria-label="University of Washington" role="navigation">
                                        <Nav.Link href='https://myuw.washington.edu' target="_blank">MyUW</Nav.Link>
                                        <Nav.Link href='http://www.washington.edu/' target="_blank">UW.edu</Nav.Link>
                                    </Nav>
                                </Navbar>
                                <div className="bg-purple px-3 py-2">
                                    <div className="d-flex align-items-center">
                                        <a href="/" className="navbar-brand text-white fs-1">{this.props.title}</a>
                                    </div>
                                </div>
                            </header>
                            <Container fluid className="app-login bg-white pb-5">
                                <Row>
                                    <Col xs={12} md={10} lg={8} className="mx-auto">
                                        <main role="main" className="content" id="main-content">
                                            <div className="text-center mt-5 mb-5">
                                                <h1>Sign in to start using {this.props.title}</h1>
                                            </div>
                                            <Row className="row-divided mb-3">
                                                <Col xs={10} sm={6} className="mx-auto clearfix">
                                                    <h2>Already have a UW NetID?</h2>
                                                    <Button id="login-netid" onClick={this.loginNetid} variant="login-netid" className="d-flex d-sm-inline-flex align-items-center p-3">
                                                        <WashingtonW size="4rem" aria-hidden={true} />
                                                        <div className="ms-4">
                                                            <strong className="d-block">UW NetID</strong>
                                                            <small className="d-block text-uppercase">Weblogin</small>
                                                        </div>
                                                    </Button>
                                                    <h3 className="fst-italic mt-3">Best option for:</h3>
                                                    <ul>
                                                        <li>Current UW students</li>
                                                        <li>Former UW students</li>
                                                        <li>UW faculty or staff</li>
                                                        <li>Recently admitted students</li>
                                                    </ul>
                                                </Col>
                                                <div className="vertical-divider d-none d-sm-block">or</div>
                                                <Col xs={10} sm={6} className="mx-auto clearfix mt-3 mt-sm-0">
                                                    <h2>Not a UW student?</h2>
                                                    <div className="d-flex flex-column d-grid gap-3">
                                                        <Button id="login-google" onClick={this.loginGoogle} variant="login-google" className="d-flex align-items-center text-white">
                                                            <Google aria-hidden={true} className="fs-5 mx-2" />
                                                            <span>Sign in with Google</span>
                                                        </Button>
                                                    </div>
                                                    <h3 className="fst-italic mt-3">Best option for:</h3>
                                                    <ul>
                                                        <li>Community college students</li>
                                                        <li>Technical college students</li>
                                                        <li>High school students</li>
                                                        <li>Parents</li>
                                                    </ul>
                                                </Col>
                                            </Row>
                                            <div>
                                                <p>
                                                    <small><strong>Important:</strong> To protect your privacy and prevent unauthorized use,
                                                        completely exit your browser when you are finished using {this.props.title}. You still may be logged in
                                                        to other personalized UW websites you visited this session. Need help?
                                                        Review <a href="http://www.washington.edu/computing/weblogin/logout.html" target="_blank">how to log out</a>.
                                                    </small>
                                                </p>
                                                <p>
                                                    <small><strong>UW Advisers:</strong> If you reach this page while viewing a student's plan, please
                                                        completely exit your browser, return to EARS or MyGrad, and select the student's plan link
                                                        to continue.
                                                    </small>
                                                </p>
                                            </div>
                                        </main>
                                    </Col>
                                </Row>
                            </Container>
                            <footer className="d-print-none d-grid gap-3 p-3 text-center fs-7 bg-navigate border border-3 border-start-0 border-end-0 border-bottom-0" role="contentinfo">
                                <ul className="list-inline list-inline-pipe-separated m-0">
                                    <li className="list-inline-item"><a href="https://itconnect.uw.edu/tools-services-support/academic-planning/myplan-academic-planner/" target="_blank">MyPlan Help</a></li>
                                    <li className="list-inline-item"><a href="https://itconnect.uw.edu/help/" target="_blank">Contact</a></li>
                                    <li className="list-inline-item"><a href="http://www.washington.edu/online/terms" target="_blank">Terms</a></li>
                                    <li className="list-inline-item"><a href="http://www.washington.edu/online/privacy" target="_blank">Privacy</a></li>
                                </ul>
                                <ul className="list-inline m-0">
                                    <li className="list-inline-item">&copy; {new Date().getFullYear()} University of Washington</li>
                                </ul>
                            </footer>
                        </div>
                    </DocumentTitle>;
                break;
            default:
                component = <UnknownState name="Login"/>;
        }

        return component;
    };
}

Login.propTypes = {
    title: PropTypes.string.isRequired
}

export default observer(Login);
